/**
 * SolMPriceAdmin — inline price manager for Sol-Millennium SKUs.
 *
 * Scope (per spec):
 *   • List every Sol-M variant with pack summary and current effective price.
 *   • Filter to "unpriced only" so admins can zero in on missing prices fast.
 *   • Inline edit: unit_price + case_price; missing side auto-computed from
 *     the pack's total_units.
 *   • Save is a single PUT — no re-import needed.
 *   • Super-admin / owner only (backend enforces via require_role).
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Search, Save, Trash2, DollarSign, Filter } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const t = localStorage.getItem("sterilemate_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function num(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function fmt(n) {
  if (n === null || n === undefined) return "—";
  return `$${Number(n).toFixed(2)}`;
}

/**
 * Row-level state so each SKU edits independently.
 */
function PriceRow({ row, onSaved }) {
  const [unit, setUnit] = useState(row.unit_price ?? "");
  const [caseP, setCaseP] = useState(row.case_price ?? "");
  const [saving, setSaving] = useState(false);
  const totalUnits = row.pack?.total_units || null;

  // Reset if parent row changes (fetch refresh)
  useEffect(() => {
    setUnit(row.unit_price ?? "");
    setCaseP(row.case_price ?? "");
  }, [row.unit_price, row.case_price]);

  const dirty =
    String(unit || "") !== String(row.unit_price ?? "") ||
    String(caseP || "") !== String(row.case_price ?? "");

  // Auto-compute the other side while typing (client-side preview only —
  // backend recomputes authoritatively on save).
  const preview = useMemo(() => {
    const u = num(unit);
    const c = num(caseP);
    if (u && !c && totalUnits) return { unit: u, case: +(u * totalUnits).toFixed(2) };
    if (c && !u && totalUnits) return { unit: +(c / totalUnits).toFixed(4), case: c };
    return { unit: u, case: c };
  }, [unit, caseP, totalUnits]);

  const save = async () => {
    const u = num(unit);
    const c = num(caseP);
    if (!u && !c) {
      toast.error("Enter a unit price or a case price greater than 0.");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/solm/products/${encodeURIComponent(row.sku)}/price`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ unit_price: u, case_price: c }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      toast.success(`Saved ${row.sku}`);
      onSaved?.(row.sku, { ...data.effective, override_updated_at: data.stored?.updated_at, override_updated_by: data.stored?.updated_by });
    } catch (e) {
      toast.error(`Save failed: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm(`Remove price override for ${row.sku}?`)) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/solm/products/${encodeURIComponent(row.sku)}/price`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!res.ok && res.status !== 404) throw new Error(`HTTP ${res.status}`);
      toast.success(`Cleared ${row.sku}`);
      onSaved?.(row.sku, { unit_price: null, case_price: null, has_price: false });
      setUnit("");
      setCaseP("");
    } catch (e) {
      toast.error(`Clear failed: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr
      className={row.has_price ? "" : "bg-amber-50/40"}
      data-testid={`solm-price-row-${row.sku}`}
    >
      <td className="p-2 align-top w-14">
        {row.image ? (
          <img src={row.image} alt="" className="w-12 h-12 object-contain bg-white rounded border border-slate-200" />
        ) : (
          <div className="w-12 h-12 bg-slate-100 rounded" />
        )}
      </td>
      <td className="p-2 align-top">
        <p className="text-[11px] font-mono text-slate-500">{row.sku}</p>
        <p className="text-sm font-semibold text-slate-900 leading-snug line-clamp-2" title={row.variant_name}>
          {row.variant_name || row.product_name}
        </p>
        <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-1">{row.category_name}</p>
        {row.pack?.display && (
          <p className="mt-1 text-[10px] font-semibold text-sky-800 bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5 inline-block">
            {row.pack.display}
          </p>
        )}
      </td>
      <td className="p-2 align-top w-32">
        <div className="relative">
          <DollarSign className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
          <Input
            type="number"
            step="0.0001"
            min="0"
            value={unit}
            placeholder={preview.unit ? `~ ${preview.unit}` : "unit"}
            onChange={(e) => setUnit(e.target.value)}
            className="pl-6 h-8 text-sm"
            data-testid={`solm-unit-input-${row.sku}`}
          />
        </div>
      </td>
      <td className="p-2 align-top w-32">
        <div className="relative">
          <DollarSign className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
          <Input
            type="number"
            step="0.01"
            min="0"
            value={caseP}
            placeholder={preview.case ? `~ ${preview.case}` : "case"}
            onChange={(e) => setCaseP(e.target.value)}
            className="pl-6 h-8 text-sm"
            data-testid={`solm-case-input-${row.sku}`}
          />
        </div>
      </td>
      <td className="p-2 align-top w-24 text-xs text-slate-500">
        {row.override_updated_at ? (
          <>
            <p>{new Date(row.override_updated_at).toLocaleDateString()}</p>
            <p className="truncate" title={row.override_updated_by}>{row.override_updated_by}</p>
          </>
        ) : (
          <span className="italic text-slate-400">never</span>
        )}
      </td>
      <td className="p-2 align-top w-36 text-right whitespace-nowrap">
        <Button size="sm" onClick={save} disabled={!dirty || saving} data-testid={`solm-save-${row.sku}`}>
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1" /> Save</>}
        </Button>
        {row.has_price && (
          <Button
            size="sm"
            variant="ghost"
            onClick={clear}
            disabled={saving}
            className="ml-1 text-rose-500 hover:text-rose-700"
            data-testid={`solm-clear-${row.sku}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
      </td>
    </tr>
  );
}

export default function SolMPriceAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [unpricedOnly, setUnpricedOnly] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API}/api/admin/solm/products${unpricedOnly ? "?unpriced_only=true" : ""}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(data.rows || []);
    } catch (e) {
      toast.error(`Load failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [unpricedOnly]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(
      (r) =>
        r.sku?.toLowerCase().includes(s) ||
        r.variant_name?.toLowerCase().includes(s) ||
        r.product_name?.toLowerCase().includes(s) ||
        r.category_name?.toLowerCase().includes(s)
    );
  }, [q, rows]);

  const onRowSaved = (sku, patch) => {
    setRows((prev) => prev.map((r) => (r.sku === sku ? { ...r, ...patch, has_price: !!(patch.unit_price || patch.case_price) } : r)));
    // If we're filtered to unpriced-only, drop it out of view once priced
    if (unpricedOnly && (patch.unit_price || patch.case_price)) {
      setRows((prev) => prev.filter((r) => r.sku !== sku));
    }
  };

  const unpricedCount = rows.filter((r) => !r.has_price).length;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="solm-price-admin">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-black text-slate-900">Sol-Millennium — Manual Pricing</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            All Sol-M items are sold by the case. Enter <b>unit</b> or <b>case</b> price — we
            auto-compute the missing side from the parsed pack summary
            (<code>total_units / units_per_box</code>). Saves persist in MongoDB and immediately
            override the catalog JSON in every view (marketplace, cart, PDF quote).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setUnpricedOnly((v) => !v)}
            className={`text-xs font-semibold px-3 py-2 rounded-lg border inline-flex items-center gap-1.5 ${
              unpricedOnly
                ? "bg-amber-100 border-amber-400 text-amber-900"
                : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
            }`}
            data-testid="solm-toggle-unpriced"
          >
            <Filter className="w-3.5 h-3.5" />
            {unpricedOnly ? `Showing unpriced (${unpricedCount})` : "Showing all"}
          </button>
        </div>
      </div>

      <div className="mb-4 relative max-w-md">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by SKU, name, or category…"
          className="pl-9"
          data-testid="solm-price-search"
        />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
        <div className="max-h-[70vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 border-b border-slate-200 z-10">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-600">
                <th className="p-2 w-14">Img</th>
                <th className="p-2">SKU · Product · Pack</th>
                <th className="p-2 w-32">Unit Price</th>
                <th className="p-2 w-32">Case Price</th>
                <th className="p-2 w-24">Last update</th>
                <th className="p-2 w-36 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={6} className="p-12 text-center text-slate-500">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading Sol-M catalog…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-12 text-center text-slate-500">
                    {unpricedOnly
                      ? "No unpriced Sol-M SKUs — everything has a price. Toggle to Show All to review."
                      : "No SKUs match your search."}
                  </td>
                </tr>
              ) : (
                filtered.map((r) => <PriceRow key={r.sku} row={r} onSaved={onRowSaved} />)
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
